import React, { useEffect, useRef } from 'react';
import { Form } from 'new/elements/Form';
import { SelectConnectionPage } from 'new/components/flows/types/cdc/CreateCDCFlow/SelectConnectionPage';
import { EnterCredentialPage } from 'new/components/flows/types/cdc/CreateCDCFlow/EnterCredentialPage';
import { ResourceSetupPage } from 'new/components/flows/types/cdc/CreateCDCFlow/ResourceSetupPage';
import { useSelector } from 'react-redux';
import { FORM_TYPE_CREATE_CDC } from 'js/constants/formTypes';
import { Header } from 'new/elements/Form/Header';
import { flowDataMap } from 'new/elements/flows/dataMap';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import * as PATHS from 'js/constants/pathnames';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import flowTypes from 'new/components/flows/types/flowTypes';
import FlowParamsProvider from 'new/components/flows/FlowParamsProvider';
import { HeaderMetaConfigProvider } from 'new/components/flows/context/HeaderMetaConfigProvider';
import useFlowParams from 'new/components/flows/useFlowParams';

interface CreateFlowProps {
  isModal?: boolean;
  nexsetId?: number;
  onDismiss?: (data: any) => void;
}

const CreateFlow = (props: CreateFlowProps) => {
  const { resourceType, id, isModal, onDismiss } = useFlowParams();
  const start = useRef(true);

  const {
    updateFormData,
    resetFormData,
    end: endForm,
    start: startForm,
    data,
  } = useFlowsSetup(FORM_TYPE_CREATE_CDC, flowTypes.cdc);

  const { connectionType } =
    useSelector((state: any) => state.forms[FORM_TYPE_CREATE_CDC]?.data) || {};

  const flowMap = flowDataMap.cdc;

  if (start.current) {
    startForm();
    updateFormData({
      connectionType: resourceType.toUpperCase(),
      flowType: flowTypes.cdc,
    });
    start.current = false;
  }

  function resetFields() {
    resetFormData([
      'credentialId',
      'selectedCredentialInfo',
      'selectedConnection',
      'selectedBucket',
      'selectedTable',
      'table',
      'source',
      'sink',
      'nexset',
      'isNewTable',
      'sourceConfig',
      'sinkConfig',
    ]);
  }

  useEffect(() => {
    resetFields();
    return () => {
      resetFields();
      if (connectionType === 'SINK') {
        endForm();
      }
    };
  }, []);

  const onFormDismiss = () => {
    if (isModal) {
      onDismiss?.(data);
      return true;
    }
    endForm();

    // Don't return true - let FormLeaveConfirmation handle navigation
    // It will check for returnPath, project context, history, or navigate to dashboard
    return false;
  };

  const isEditMode = !!id;
  const type = resourceType === 'source' ? 'Source' : 'Destination';

  return (
    <HeaderMetaConfigProvider>
      <Form
        formType={FORM_TYPE_CREATE_CDC}
        isModal={isModal}
        isEditMode={isEditMode}
        onDismiss={onFormDismiss}
        startPage={isEditMode ? 2 : undefined}
        HeaderComponent={
          <Header
            hideContextualMenu={isModal}
            title={
              <>
                {flowMap.shortTitle} <span style={{ color: 'var(--color-greyscale-200)' }}>/</span>{' '}
                {isEditMode ? `Editing Data ${type}` : `New Data ${type}`}
              </>
            }
            description=""
          />
        }
      >
        <SelectConnectionPage />
        <EnterCredentialPage />
        <ResourceSetupPage />
      </Form>
    </HeaderMetaConfigProvider>
  );
};

const CreateCDCFlowContent = (props: CreateFlowProps) => {
  return (
    <FlowParamsProvider {...props}>
      <CreateFlow {...props} />
    </FlowParamsProvider>
  );
};

CreateCDCFlowContent.screenConfig = {
  screenName: 'CreateCDCFlow',
  domain: 'main',
  path: [
    `${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/cdc/:resourceType/new`,
    `${ROUTE_CONNECT}${PATHS.ROUTE_PIPELINES}/cdc/:resourceType/:id/edit`,
  ],
  hideSideBar: true,
};

export default CreateCDCFlowContent;
